import { Country } from '../country';
import { Currency } from '../currency';
import { Money } from '../salesItem';
import { ServiceDepartmentMinimal } from '../serviceDepartment';
import { ServiceOrganizationListItem } from '../serviceOrganization';
import { UserData } from '../token';
import { UserMinimalInfo } from '../user';
import { UserGroupMinimal } from '../userGroup';

export const orderShopType = {
  tire_quote: 'tire_quote',
  rim_quote: 'rim_quote'
} as const;

export type OrderShopType = (typeof orderShopType)[keyof typeof orderShopType];

export const orderShopTypeLabel: Record<OrderShopType, string> = {
  [orderShopType.tire_quote]: 'Tire quote',
  [orderShopType.rim_quote]: 'Rim quote'
} as const;

export const orderShopStatus = {
  ACTIVE: 0,
  CANCELLED: 1,
  INVOICED: 2
} as const;

export const orderShopStatusToLabel: Record<OrderShopStatusType, string> = {
  [orderShopStatus.ACTIVE]: 'Active',
  [orderShopStatus.CANCELLED]: 'Cancelled',
  [orderShopStatus.INVOICED]: 'Invoiced'
} as const;

export const orderShopStatusOptions = Object.values(orderShopStatus).map((status) => ({
  label: orderShopStatusToLabel[status],
  value: status
}));

export type OrderShopStatusType = (typeof orderShopStatus)[keyof typeof orderShopStatus];

interface OrderShopStatus {
  value: OrderShopStatusType;
  label: string;
}

interface CustomerGroupMember {
  userGroup: UserGroupMinimal;
  user: UserData;
}

interface CommonOrderShop {
  id: number;
  slug: string;
  type: OrderShopType;
  createdAt: string;
  updatedAt: string;
  createdBy: UserMinimalInfo;
  updatedBy: UserMinimalInfo;
  customerGroupMember: CustomerGroupMember;
  isPaid: boolean;
  status: OrderShopStatus;
  totalPriceNet: Money;
  amountDue: number;
  amountPaid: number;
  amountVat: number;
}

interface OrderLineShop {
  id: number;
  description: string;
  descriptionInternal: string;
  pricePerUnitNet: number;
  quantity: number;
  vatPercentage: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: UserMinimalInfo;
  createdBy: UserMinimalInfo;
}

export interface OrderShopReceipt {
  pdfDocument: { url: string };
  serviceOrganization: ServiceOrganizationListItem;
  createdAt: string;
  id: number;
}
export interface OrderShopListItem extends CommonOrderShop {}

export interface OrderShop extends CommonOrderShop {
  referenceId: number;
  commentsCustomer: string;
  commentsStaff: string;
  vatPercentage: number;
  orderLines: OrderLineShop[];
  currency: Currency;
  country: Country;
  serviceDepartment: ServiceDepartmentMinimal;
  receipts: OrderShopReceipt[];
}

export interface OrderShopFilters {
  statuses?: OrderShopStatusType[];
  orderTransactionStatuses?: OrderShopTransactionStatus[];
  serviceDepartmentIds?: number[];
}

export const orderShopTransactionStatus = {
  INITIALIZED: 0,
  ON_HOLD: 1,
  DECLINED: 2,
  AUTHORIZED: 3,
  UNKNOWN: 4,
  FAILED: 5,
  AUTHORIZATION_VOIDED: 6,
  PARTIALLY_CAPTURED: 7,
  CAPTURED: 8,
  PARTIALLY_CAPTURE_REFUNDED: 9,
  PARTIALLY_REFUNDED: 10,
  REFUNDED: 11
} as const;

export type OrderShopTransactionStatus = (typeof orderShopTransactionStatus)[keyof typeof orderShopTransactionStatus];

export const orderShopTransactionStatusToLabel: Record<OrderShopTransactionStatus, string> = {
  [orderShopTransactionStatus.INITIALIZED]: 'Initialized',
  [orderShopTransactionStatus.ON_HOLD]: 'On hold',
  [orderShopTransactionStatus.DECLINED]: 'Declined',
  [orderShopTransactionStatus.AUTHORIZED]: 'Authorized',
  [orderShopTransactionStatus.UNKNOWN]: 'Unknown',
  [orderShopTransactionStatus.FAILED]: 'Failed',
  [orderShopTransactionStatus.AUTHORIZATION_VOIDED]: 'Authorization voided',
  [orderShopTransactionStatus.PARTIALLY_CAPTURED]: 'Partially captured',
  [orderShopTransactionStatus.CAPTURED]: 'Captured',
  [orderShopTransactionStatus.PARTIALLY_CAPTURE_REFUNDED]: 'Partially capture refunded',
  [orderShopTransactionStatus.PARTIALLY_REFUNDED]: 'Partially refunded',
  [orderShopTransactionStatus.REFUNDED]: 'Refunded'
} as const;

export const orderShopTransactionStatusOptions = Object.values(orderShopTransactionStatus).map((status) => ({
  label: orderShopTransactionStatusToLabel[status],
  value: status
}));
